import React, { useState, useEffect } from "react";
import { nexusTvLogin } from "../lib/api";
import NexusTV from "./NexusTV";
import NexusEye from "./NexusEye";
import NexusIntel from "./NexusIntel";
import NexusLife from "./NexusLife";
import NexusAnnouncer from "./NexusAnnouncer";
import NexusRemote from "./NexusRemote";
import TvPicker from "./tv/TvPicker";
import TvScreenB from "./tv/TvScreenB";
import FloorCommandRoom from "./floor/FloorCommandRoom";
import { Loader2, Lock, Cpu, LayoutDashboard, HeartPulse } from "lucide-react";

const STORAGE_KEY = "nexus_tv_session";
const SCREENS = [
  { id: "presence", label: "PRESENCE", icon: Cpu },
  { id: "intel", label: "INTEL", icon: LayoutDashboard },
  { id: "life", label: "LIFE", icon: HeartPulse },
];

export default function NexusTvGate({ mode = "tv" }) {
  const [session, setSession] = useState(null);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [checked, setChecked] = useState(false);
  const initialScreen = new URLSearchParams(window.location.search).get("screen") || "presence";
  const [screen, setScreen] = useState(["presence", "intel", "life"].includes(initialScreen) ? initialScreen : "presence");

  const changeScreen = (id) => {
    setScreen(id);
    const u = new URL(window.location.href);
    u.searchParams.set("screen", id);
    window.history.replaceState({}, "", u);
  };

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && saved.role === "master") setSession(saved);
    } catch (e) {}
    setChecked(true);
  }, []);

  const submit = async () => {
    setLoading(true); setErr("");
    try {
      const res = await nexusTvLogin(pin.trim());
      if (res && res.ok && res.role === "master") {
        setSession(res);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(res));
      } else {
        setErr(res?.error_text || "Wrong passcode. Try again.");
      }
    } catch (e) {
      setErr("Wrong passcode. Try again.");
    } finally { setLoading(false); }
  };

  if (!checked) {
    return <div className="min-h-screen bg-[#030712] flex items-center justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  }

  if (session && mode === "remote") return <NexusRemote session={session} />;
  if (session && mode === "tv-pick") return <TvPicker />;
  if (session && mode === "tv-b") return <TvScreenB session={session} />;
  if (session && mode === "tv-floor") return <div className="h-screen w-screen overflow-hidden" data-testid="tv-floor"><FloorCommandRoom session={session} tv /></div>;
  if (session && mode === "tv-a") return <><NexusAnnouncer userId={session.user_id} /><NexusTV session={session} screen="a" /></>;
  if (session) {
    return (
      <div className="min-h-screen bg-[#030712] nx-display">
        <NexusAnnouncer userId={session.user_id} />
        <div className="h-[52px] flex items-center justify-center gap-2 border-b border-[#00F0FF22] bg-[#050b18]" data-testid="nexus-screen-switcher">
          {SCREENS.map((sc) => {
            const Icon = sc.icon; const active = screen === sc.id;
            return (
              <button key={sc.id} onClick={() => changeScreen(sc.id)} data-testid={`nexus-screen-${sc.id}`}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full nx-mono text-xs tracking-widest font-bold transition-colors ${active ? "bg-[#00F0FF] text-[#030712]" : "text-cyan-200/50 border border-cyan-200/15 hover:text-cyan-100"}`}>
                <Icon className="w-3.5 h-3.5" /> {sc.label}
              </button>
            );
          })}
        </div>
        {screen === "intel" ? <NexusIntel session={session} />
          : screen === "life" ? <NexusLife session={session} />
          : mode === "eye" ? <NexusEye session={session} /> : <NexusTV session={session} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] nx-display nx-scanlines flex items-center justify-center p-6">
      <div className="w-full max-w-sm nx-glass rounded-3xl p-8 space-y-5 relative" style={{ boxShadow: "0 0 60px #00F0FF22" }}>
        <div className="absolute top-0 left-0 w-6 h-6 nx-bracket-tl rounded-tl-lg" />
        <div className="absolute bottom-0 right-0 w-6 h-6 nx-bracket-br rounded-br-lg" />
        <div className="text-center space-y-2">
          <div className="nx-orbitron text-4xl font-black tracking-[0.3em] text-[#00F0FF]" style={{ textShadow: "0 0 24px #00F0FF66" }}>NEXUS</div>
          <p className="text-cyan-100/60 nx-mono text-sm flex items-center justify-center gap-2"><Lock className="w-4 h-4" /> ENTER MASTER PASSCODE</p>
        </div>
        <input
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
          onKeyDown={(e) => { if (e.key === "Enter" && pin.length >= 3) submit(); }}
          inputMode="numeric" type="password" autoFocus placeholder="•••••" data-testid="nexus-tv-passcode"
          className="w-full text-center text-4xl nx-orbitron font-black tracking-[0.4em] bg-[#030712] text-[#00F0FF] border-2 border-[#00F0FF55] rounded-2xl py-4 placeholder:text-cyan-100/20 focus:outline-none focus:border-[#00F0FF]" />
        {err && <p className="text-red-400 text-sm text-center nx-mono" data-testid="nexus-tv-passcode-err">{err}</p>}
        <button onClick={submit} disabled={loading || pin.length < 3} data-testid="nexus-tv-passcode-submit"
          className="w-full py-4 rounded-2xl nx-orbitron font-bold tracking-widest uppercase bg-[#00F0FF] text-[#030712] disabled:opacity-30 hover:scale-[1.02] transition-transform flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enter"}
        </button>
        <p className="text-center text-xs text-cyan-100/40 nx-mono">Bookmark this page on your TV for instant access.</p>
      </div>
    </div>
  );
}
